import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { CategoryData, getExportingOptions } from '../utils/jsonDataProcessing';
import { JSONRecord } from '../utils/jsonDataProcessing';

interface KeywordsByCategoryChartProps {
  categoryData: CategoryData;
  parsedData: JSONRecord[];
  showAllKeywords: boolean;
}

const KeywordsByCategoryChart: React.FC<KeywordsByCategoryChartProps> = ({ 
  categoryData, 
  parsedData, 
  showAllKeywords 
}) => {
  // Helper to check if a record has a specific category
  const hasCategory = (record: JSONRecord, category: string): boolean => {
    // Check in topics
    if (record.classification.topics.some(t => t.name === category)) return true;
    // Check in subtopics
    if (record.classification.subtopics.some(s => s.name === category)) return true;
    return false;
  };

  const displayData = showAllKeywords ? parsedData : parsedData.slice(0, 20);

  const options = {
    chart: {
      type: 'column',
      height: '400px',
      zoomType: 'x',
    },
    title: {
      text: ''
    },
    xAxis: {
      categories: displayData.map(record => record.query),
      title: {
        text: ''
      },
      labels: {
        rotation: -45,
        style: {
          fontSize: '11px'
        },
        formatter: function() {
          // Truncate long keyword names
          const value = this.value as string;
          return value.length > 15 ? value.substring(0, 15) + '...' : value;
        },
        useHTML: true
      },
      scrollbar: {
        enabled: showAllKeywords
      },
      min: 0,
      max: showAllKeywords ? undefined : 19
    },
    yAxis: {
      min: 0,
      max: 1,
      title: {
        text: 'Category Assignment'
      },
      stackLabels: {
        enabled: true
      }
    },
    legend: {
      title: {
        text: '' // Ensure the title is empty
      },
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      x: 0,
      y: 0,
      itemWidth: 200,
      width: 400,
      useHTML: true,
      labelFormatter: function(this: Highcharts.SeriesLegendItemClickEventObject): string {
        // Add title attribute to show full name on hover
        return '<span title="' + this.name + '">' + this.name + '</span>';
      }
    },
    tooltip: {
      formatter: function(this: Highcharts.TooltipFormatterContextObject) {
        return '<b>' + this.x + '</b><br>' +
               this.series.name + ': ' + (this.y === 1 ? 'Yes' : 'No');
      },
      useHTML: true
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false
        }
      }
    },
    series: categoryData.categories.map((category) => ({
      name: category,
      data: displayData.map(record => hasCategory(record, category) ? 1 : 0)
    })),
    ...getExportingOptions('Keywords By Category')
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default KeywordsByCategoryChart;
